<template>
  <div
    :class="`owal-banner-button__container_${destructive === true ? 'destructive' : 'default'}`"
    :style="dynamicStyle"
    @click="handleClick"
    >
    <div class="tooltip left" v-if="tooltip && tooltipDirection === 'left'">
      {{ tooltip }}
    </div>
    <slot>
      <div class="owal-banner-button__text">
        <a
          v-if="url && isUrlAbsolute(url)"
          :class="`owal-banner-button__title_${alignment}`"
          :href="url"
          target="_blank"
          >
          {{ title }}
        </a>
        <div v-else :class="`owal-banner-button__title_${alignment}`">
          {{ title }}
        </div>
        <div :class="`owal-banner-button__subtitle_${alignment}`">
          {{ subtitle }}
        </div>
      </div>
      <slot name="accessory">
        <div v-if="accessoryType !== 'none'" class="owal banner button accessory">
          <font-awesome-icon
            icon="chevron-right"
            :class="`chevron ${(open && accessoryType !== 'static') ? 'rotated' : ''}`"
            />
        </div>
      </slot>
    </slot>
    <div class="tooltip right" v-if="tooltip && tooltipDirection === 'right'">
      {{ tooltip }}
    </div>
  </div>
</template>

<script>
import { isUrlAbsolute } from '../util'

export default {
  name: 'OwalButton',
  /**
   * @prop {String} identifier A string identifier which is passed to handlers so parents can identify the button.
   * @prop {String} title The title of the button.
   * @prop {String} subtitle The subtitle of the button.
   * @prop {String} accessoryType The presence and behavior of the chevron acessory.
   * @prop {Boolean} open The initial state of the accessory.
   * @prop {Function} callback A function execute when the button is clicked. If a url is supplied, this is ignored.
   * @prop {String} url An absolute URL to send the user to when they click on the button.
   * @prop {Boolean} destructive
   */
  props: {
    identifier: {
      type: String,
    },
    title: {
      type: String,
    },
    subtitle: {
      type: String,
    },
    tooltip: {
      type: String,
    },
    tooltipDirection: {
      type: String,
      default: 'left',
    },
    /**
     * Valid values:
     *  - `none`: no chevron
     *  - `static`: chevron is present but does not animate.
     *  - `dynamic`: The chevron is present and alternates position based on button state.
     */
    accessoryType: {
      type: String,
      default: 'none',
      validation: function (newValue) {
        const validValues = ['none', 'static', 'dynamic']
        const valid = validValues.includes(newValue)

        if (!valid) {
          console.log(`OwalScrollContainer.vue: Valid values are ${validValues.join(',')}`)
        }

        return valid
      },
    },
    open: {
      type: Boolean,
      default: false,
    },
    callback: {
      type: Function,
      default: undefined,
    },
    url: {
      type: String,
      default: undefined,
    },
    destructive: {
      type: Boolean,
      default: false,
    },
    alignment: {
      type: String,
      default: 'left',
      validation: function (newValue) {
        return ['left', 'right', 'center'].includes(newValue)
      },
    },
    corners: {
      type: Object,
      default: () => {
        const radius = '0.35rem'
        return {
          topLeft: radius,
          topRight: radius,
          bottomRight: radius,
          bottomLeft: radius,
        }
      },
    },
  },
  computed: {
    dynamicStyle () {
      return `border-radius: ${this.corners.topLeft} ${this.corners.topRight} ${this.corners.bottomRight} ${this.corners.bottomLeft};`
    },
  },
  methods: {
    handleClick () {
      if (this.url && isUrlAbsolute(this.url)) {
        console.debug(`Skipping click for button, since URL was supplied.`)
        return
      }
      const payload = {
        open: this.open,
        identifier: this.identifier || 'not set',
      }
      if (this.callback) {
        this.callback(payload)
      } else {
        this.$emit('click', payload)
      }
    },
    isUrlAbsolute,
  },
}
</script>

<style scoped>
.owal-banner-button__container_default:hover .tooltip.right {
  margin: 0rem .0rem 0rem .75rem;
}
.owal-banner-button__container_default:hover .tooltip.left {
  margin: 0rem .75rem 0rem .0rem;
}

.owal-banner-button__container_default:hover .tooltip {
  opacity: 1;
  width: inherit;
  max-width: 200px;
  color: #000000;
}

.tooltip {
  opacity: 0;
  max-width: 0;
  white-space: nowrap;
  font-size: 0.75rem;
  color: #fff;
  border-radius: 0.35rem;
  margin: 0;
  z-index: 1;
  transition: opacity 350ms ease, max-width 250ms ease;
}

.owal-banner-button__container_default,
.owal-banner-button__container_destructive {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  width: fit-content;

  padding: 0.3571428571rem;
  cursor: pointer;
  transition: background-color 300ms ease, transform 300ms ease, opacity 250ms ease;
  overflow: hidden;
  border: 1px solid #E0E0E0;
  background: #F0F0F0;
}

.owal-banner-button__container_destructive,
.owal-banner-button__container_destructive a {
  color: rgba(208,2,27, 0.9);
}

/** Default style hover. */
.owal-banner-button__container_default,
.owal-banner-button__container_default a{
  color: #324057;
}

.owal-banner-button__container_default:hover {
  background: #F3F4F5;
}

/** All Button Types Hover */
.owal-banner-button__container_default:hover,
.owal-banner-button__container_destructive:hover {
  /* transform: scale(1.05);
  opacity: 0.75; */
}

.owal-banner-button__text a {
  display: block;
  margin: inherit;
  padding: inherit;
}

/* Title: */
.owal-banner-button__title_left,
.owal-banner-button__title_right,
.owal-banner-button__title_center {
  font-family: 'Lato-Regular', 'Avenir';
  font-size: 1rem;
  letter-spacing: 0;
  padding: 0;
  margin: 0;
}

/* Subtitle: */
.owal-banner-button__subtitle_left,
.owal-banner-button__subtitle_right,
.owal-banner-button__subtitle_center {
  font-family: 'Lato-Italic', 'Avenir', sans-serif;
  font-size: 0.8571428571rem;
  color: #8492A6;
  letter-spacing: 0;
  margin: 0;
  padding: 0;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 28rem;
  overflow: hidden;
}

.owal-banner-button__title_left,
.owal-banner-button__subtitle_left {
  text-align: left;
  align-self: flex-start;
}

.owal-banner-button__subtitle_right,
.owal-banner-button__title_right {
  text-align: right;
  align-self: flex-end;
}

.owal-banner-button__title_center,
.owal-banner-button__subtitle_center {
  text-align: center;
  align-self: center;
}

/** Acessory */
.owal.banner.button.accessory {
  flex-direction: column;
  width: 0.7142857143rem;
  height: 100%;
  flex-shrink: 0;

  justify-content: center;
  align-content: center;
  align-items: center;
  margin-left: 0.71428571rem;
}

/* Chevron accessory */
.chevron {
  height: 0.7857142857rem;
  width: 0.4285714286rem;
  margin-right: 015rem;
  transition: transform 300ms ease;
  transform: initial;
}

.chevron.rotated {
  transform: translate(0, 0.15rem) rotateZ(90deg);
}

</style>
